import React, { useState, useEffect } from "react";
import BookingPopup from "./BookingPopup";
import { useLocation } from "react-router-dom";
import {
  FaPlane, FaExchangeAlt, FaInfoCircle, FaLuggageCart,
} from "react-icons/fa";
import FlippableFareCard from "../ui/FlippableFareCard";
import FlightDetailsAccordion from "./FlightDetailsAccordion";
import {
  computeDurationMinutes,
  formatLocalDateWithAdjustment,
  getTimezoneAbbreviation,
} from "../../utils/dateUtils";

export interface FareTier {
  label: string;
  price: number;
  currency: string;
  baggage: string;
  cancellation: string;
  addOns: string;
  cabinCode?: string;
  fareBasisCode?: string;
  fareType?: string;
  bookingCodes?: string[];
  scheduleDetails?: Array<{ mealCode?: string; seatsAvailable?: number }>;
  brandFeatures?: Array<{ id: number; commercialName: string }>;
  fullGroup?: any;
  changePolicy?: {
    changeable: boolean;
    changeFee?: { amount: number; currency: string; type: string };
  };
  refundPolicy?: {
    refundable: boolean;
    cancellationFee?: { amount: number; currency: string };
    conditions?: string;
  };
}

interface FlightCardProps {
  onward: any;
  amount: number;
  baseFare: number;
  taxes: number;
  currency: string;
  classType: string;
  selectedClass: string;
  fareTiers: FareTier[];
  flightData?: any;
  selectedFareTier?: FareTier;
  disableExpansion?: boolean;
}

const cabinClassOptions = [
  { label: "Economy", code: "Y" },
  { label: "Premium Economy", code: "S" },
  { label: "Business", code: "C" },
  { label: "Premium Business", code: "J" },
  { label: "First Class", code: "F" },
  { label: "Premium First", code: "P" },
];

function getCabinLabel(code?: string) {
  return cabinClassOptions.find((c) => c.code === code)?.label || code || "Unknown";
}

function getRefundStatus(tier: FareTier | null): { text: string; isRefundable: boolean } {
  if (!tier) return { text: "Non-refundable", isRefundable: false };
  if (tier.refundPolicy?.refundable === true) return { text: "Fully Refundable", isRefundable: true };
  if (tier.cancellation?.toLowerCase().includes("refund")) return { text: "Refundable", isRefundable: true };
  return { text: "Non-refundable", isRefundable: false };
}

function getChangePolicy(tier: FareTier | null): string {
  if (!tier) return "Changes not allowed";
  if (tier.changePolicy?.changeable) {
    if (tier.changePolicy.changeFee) {
      return `Changeable (Fee: ${tier.changePolicy.changeFee.amount} ${tier.changePolicy.changeFee.currency})`;
    }
    return "Free changes allowed";
  }
  return "Changes not allowed";
}

// Extract HH:MM from "HH:MM:SS±HH:MM" timezone-offset strings
function extractTime(dateTimeStr: string): string {
  if (!dateTimeStr) return "--:--";
  const match = dateTimeStr.match(/(\d{2}:\d{2})/);
  return match ? match[1] : dateTimeStr;
}

function formatElapsedTime(minutes?: number): string {
  if (!minutes || isNaN(minutes)) return "-";
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

const FlightCard = ({
  onward,
  amount,
  baseFare,
  taxes,
  currency,
  classType,
  fareTiers = [],
  selectedClass,
  flightData,
  selectedFareTier,
  disableExpansion = false,
}: FlightCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBookingPopup, setShowBookingPopup] = useState(false);
  const location = useLocation();

  const isPassengerDetailPage = location.pathname.includes("passenger-details");
  const baseDepartureDate = location.state?.departureDate;

  const outboundLeg =
    flightData?.legs?.find((l: any) => l.direction === "outbound") ?? flightData?.legs?.[0];
  const schedules = outboundLeg?.segments ?? flightData?.onwardFlight?.schedules ?? [];
  const firstSegment = schedules[0];

  // ─── Map fareOptions → FareTier ───────────────────────────────────────────
  const rawFareOptions = flightData?.fareOptions;
  const rawOldBrandedFares = flightData?.onwardBrandedFares;

  const onwardBrandedFares: FareTier[] = Array.isArray(rawFareOptions)
    ? rawFareOptions
      .filter((fo: any) => fo != null)
      .map((fo: any) => {
        const rawCabinCode: string | undefined =
          firstSegment?.bookingInfo?.cabinCode ?? firstSegment?.cabinCode ?? undefined;
        const rawCabinClass: string | undefined = fo.cabinClass ?? undefined;
        const cabinClassNameToCode: Record<string, string> = {
          economy: "Y", premiumeconomy: "S", business: "C",
          premiumbusiness: "J", first: "F", premiumfirst: "P",
        };
        const resolvedCabinCode =
          rawCabinCode ??
          (rawCabinClass
            ? cabinClassNameToCode[rawCabinClass.toLowerCase().replace(/\s+/g, "")]
            : undefined);
        return {
          label: fo.name,
          price:
            fo.passengerPrices?.find((p: any) => p.type === "ADT")?.total ??
            fo.price?.total ?? 0,
          currency: fo.price?.currency,
          baggage:
            fo.baggage?.checked?.description ??
            fo.baggage?.cabin?.description ?? "",
          cancellation:
            fo.refundPolicy?.conditions ??
            (fo.refundPolicy?.refundable
              ? "Refundable"
              : fo.refundPolicy?.cancellationFee
                ? `Non-refundable (fee: ${fo.refundPolicy.cancellationFee.amount} ${fo.refundPolicy.cancellationFee.currency})`
                : "Non-refundable"),
          addOns: fo.amenities?.map((a: any) => a.description).join(", ") ?? "",
          cabinCode: resolvedCabinCode,
          fareBasisCode: fo.fareBasis?.code,
          fareType: fo.fareBasis?.type,
          bookingCodes: fo.bookingClasses,
          brandFeatures: fo.amenities?.map((a: any, i: number) => ({
            id: i,
            commercialName: a.description,
          })),
          fullGroup: fo,
          refundPolicy: fo.refundPolicy,
          changePolicy: fo.changePolicy,
        };
      })
    : Array.isArray(rawOldBrandedFares)
      ? rawOldBrandedFares
        .flat()
        .filter((ft: any) => ft != null)
        .map((ft: any) => ({
          label: ft.brandName ?? "",
          price: ft.price ?? 0,
          currency: ft.currency,
          baggage: ft.baggage ?? "",
          cancellation: ft.cancellation ?? "See fare rules",
          addOns: ft.addOns ?? "",
          cabinCode: ft.cabinCode,
          fareBasisCode: ft.fareBasisCode,
          fareType: ft.fareType,
          bookingCodes: ft.bookingCodes,
          scheduleDetails: ft.scheduleDetails,
          brandFeatures: ft.brandFeatures,
          fullGroup: ft,
          refundPolicy: ft.refundPolicy,
          changePolicy: ft.changePolicy,
        }))
      : [];

  const [selectedIndex, setSelectedIndex] = useState(0);
  const tier = selectedFareTier ?? onwardBrandedFares[selectedIndex] ?? null;

  useEffect(() => {
    setSelectedIndex(0);
  }, [flightData?._uniqueKey]);

  const displayedSeats =
    firstSegment?.bookingInfo?.seatsAvailable ?? firstSegment?.seatsAvailable ?? "-";
  const displayedMeal =
    firstSegment?.bookingInfo?.meal?.code ?? firstSegment?.mealCode;

  const fareOptionBaggage =
    flightData?.fareOptions?.[0]?.baggage?.checked?.description ??
    flightData?.fareOptions?.[0]?.baggage?.cabin?.description ??
    flightData?.onwardBrandedFares?.[0]?.[0]?.baggage ??
    flightData?.onwardBrandedFares?.[0]?.baggage ??
    "";

  const displayedBaggage = isPassengerDetailPage
    ? firstSegment?.bookingInfo?.baggage || firstSegment?.baggage || fareOptionBaggage || ""
    : tier?.baggage ?? "";

  const displayedClass = isPassengerDetailPage
    ? firstSegment?.bookingInfo?.cabinCode || firstSegment?.cabinCode
      ? getCabinLabel(firstSegment?.bookingInfo?.cabinCode ?? firstSegment?.cabinCode)
      : classType
    : tier?.cabinCode
      ? getCabinLabel(tier.cabinCode)
      : classType;

  const displayedPrice = tier?.price ?? amount;
  const displayedCurrency = tier?.currency ?? currency;
  const fareBrandName = tier?.label || "";
  const refundStatus = getRefundStatus(tier);
  const changePolicyText = getChangePolicy(tier);

  const depTime = extractTime(
    outboundLeg?.departure?.dateTime ?? flightData?.onwardFlight?.departureDateTime
  );
  const arrTime = extractTime(
    outboundLeg?.arrival?.dateTime ?? flightData?.onwardFlight?.arrivalDateTime
  );
  const isNextDay = (outboundLeg?.arrival?.dateAdjustment ?? 0) > 0;

  const getStopSummary = (segments: any[]) => {
    const stops = segments.length - 1;
    if (stops <= 0) return "Non-stop";
    const stopCodes = segments
      .slice(0, -1)
      .map((seg: any) => seg.arrival?.airport?.code ?? seg.arrival?.airport)
      .filter(Boolean);
    return `${stops} stop${stops > 1 ? "s" : ""} (${stopCodes.join(", ")})`;
  };


  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {showBookingPopup && (
        <BookingPopup
          flightData={flightData}
          onClose={() => setShowBookingPopup(false)}
          passengerCounts={location.state?.passengerCounts}
          departureDate={location.state?.departureDate}
          returnDate={location.state?.returnDate}
          selectedClass={location.state?.selectedClass}
          classOfService={classType}
          selectedOnwardFareTier={tier?.fullGroup ?? (tier ? [tier] : [])}
        />
      )}

      <div
        className={`bg-white w-full rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow overflow-hidden ${isExpanded ? "pb-2" : ""}`}
      >
        {/* ── Main row ── */}
        <div
          className={`px-4 py-4 sm:px-5 sm:py-4 ${!isPassengerDetailPage && !disableExpansion ? "cursor-pointer" : ""}`}
          onClick={() => {
            if (!isPassengerDetailPage && !disableExpansion) {
              if (isExpanded) setSelectedIndex(0);
              setIsExpanded((prev) => !prev);
            }
          }}
        >
          <div className="flex items-center gap-3 sm:gap-4">
            {/* Airline col */}
            <div className="flex flex-col items-center gap-0.5 w-16 sm:w-20 shrink-0">
              <img
                src={onward.airlineLogo}
                alt={onward.airlineName}
                className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                onError={(e) =>
                  ((e.target as HTMLImageElement).src = "https://via.placeholder.com/48?text=✈")
                }
              />
              <div className="text-[10px] sm:text-xs text-gray-600 font-medium text-center leading-tight line-clamp-2 max-w-full">
                {onward.airlineName}
              </div>
              <div className="text-[9px] sm:text-[10px] text-gray-400">{onward.flightNumber}</div>
            </div>

            {/* Route */}
            <div className="flex-1 min-w-0 flex items-center gap-2 sm:gap-4">
              {/* Departure */}
              <div className="shrink-0 text-left">
                <div className="text-xl sm:text-2xl font-bold text-gray-900 leading-none tabular-nums">
                  {depTime || "--:--"}
                  {(() => {
                    const tz = getTimezoneAbbreviation(
                      outboundLeg?.departure?.dateTime ??
                        flightData?.onwardFlight?.departureDateTime,
                    );
                    return tz ? (
                      <span className="ml-1 text-[10px] font-normal text-gray-400">
                        {tz}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="text-xs sm:text-sm font-semibold text-gray-500 mt-0.5">
                  {outboundLeg?.departure?.airport?.code ??
                    flightData?.onwardFlight?.departureAirport ?? "-"}
                </div>
                {baseDepartureDate && (
                  <div className="text-[10px] text-gray-400">
                    {formatLocalDateWithAdjustment(baseDepartureDate, 0).date}
                  </div>
                )}
              </div>

              {/* Middle: duration + stops line */}
              <div className="flex-1 flex flex-col items-center min-w-0 px-1">
                <div className="text-[10px] sm:text-xs text-gray-400 mb-0.5">
                  {formatElapsedTime(
                    outboundLeg?.duration ||
                      flightData?.onwardFlight?.elapsedTime ||
                      computeDurationMinutes(
                        outboundLeg?.departure?.dateTime ??
                          flightData?.onwardFlight?.departureDateTime,
                        outboundLeg?.arrival?.dateTime ??
                          flightData?.onwardFlight?.arrivalDateTime,
                        outboundLeg?.arrival?.dateAdjustment ?? 0,
                      )
                  )}
                </div>
                <div className="w-full flex items-center">
                  <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                  <div className="mx-1 sm:mx-1.5 text-[9px] sm:text-[10px] text-gray-500 bg-gray-100 border border-gray-200 px-1 sm:px-1.5 py-0.5 rounded-full whitespace-nowrap font-medium shrink-0">
                    {getStopSummary(schedules)}
                  </div>
                  <div className="flex-1 border-t-2 border-dashed border-gray-300" />
                </div>
                <FaPlane size={8} className="text-gray-300 mt-0.5" />
              </div>

              {/* Arrival */}
              <div className="shrink-0 text-right">
                <div className="text-xl sm:text-2xl font-bold text-gray-900 leading-none tabular-nums">
                  {arrTime || "--:--"}
                  {isNextDay && (
                    <span className="ml-0.5 text-[10px] font-normal text-orange-500 align-super">+1</span>
                  )}
                  {(() => {
                    const tz = getTimezoneAbbreviation(
                      outboundLeg?.arrival?.dateTime ??
                        flightData?.onwardFlight?.arrivalDateTime,
                    );
                    return tz ? (
                      <span className="ml-1 text-[10px] font-normal text-gray-400">
                        {tz}
                      </span>
                    ) : null;
                  })()}
                </div>
                <div className="text-xs sm:text-sm font-semibold text-gray-500 mt-0.5">
                  {outboundLeg?.arrival?.airport?.code ??
                    flightData?.onwardFlight?.arrivalAirport ?? "-"}
                </div>
                {baseDepartureDate && (
                  <div className="text-[10px] text-gray-400">
                    {formatLocalDateWithAdjustment(
                      baseDepartureDate,
                      outboundLeg?.arrival?.dateAdjustment || 0
                    ).date}
                  </div>
                )}
              </div>
            </div>

            {/* Vertical divider */}
            <div className="hidden sm:block self-stretch w-px bg-gray-200 shrink-0" />

            {/* Price + CTA */}
            <div className="flex flex-col items-end shrink-0 w-24 sm:w-36 pl-1 sm:pl-3">
              {fareBrandName && (
                <div className="text-[10px] text-primary font-semibold bg-blue-50 px-2 py-0.5 rounded-full mb-1 max-w-full truncate">
                  {fareBrandName}
                </div>
              )}
              <div className="text-[10px] text-gray-400">per adult</div>
              <div className="text-lg sm:text-2xl font-bold text-primary leading-tight tabular-nums">
                {displayedCurrency} {displayedPrice?.toLocaleString()}
              </div>
              <div className="text-[10px] text-gray-400 mb-1">incl. taxes</div>
              <div
                className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  refundStatus.isRefundable
                    ? "bg-green-50 text-green-700"
                    : "bg-orange-50 text-orange-600"
                }`}
              >
                {refundStatus.isRefundable ? "Refundable" : "Non-refundable"}
              </div>
              {displayedBaggage && (
                <div className="flex items-center gap-1 text-[10px] text-gray-500 mt-1">
                  <FaLuggageCart size={9} />
                  <span className="truncate max-w-[80px]">{displayedBaggage}</span>
                </div>
              )}
              {!isPassengerDetailPage && !disableExpansion && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isExpanded) setSelectedIndex(0);
                    setIsExpanded((prev) => !prev);
                  }}
                  className="mt-2 bg-primary text-white text-[10px] sm:text-xs font-bold px-3 py-1.5 rounded hover:bg-blue-800 transition-colors w-full text-center"
                >
                  {isExpanded ? "HIDE" : "VIEW PRICES"}
                </button>
              )}
              {displayedSeats !== "-" && !isPassengerDetailPage && (
                <div className="text-[10px] text-orange-600 mt-1 text-right">
                  {displayedSeats} seats left
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Expanded: fare selection ── */}
        {!isPassengerDetailPage && !disableExpansion && isExpanded && (
          <div className="border-t border-gray-100 bg-gray-50 px-4 sm:px-6 py-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-bold text-primary text-sm sm:text-base">Select Fare</h3>
              <div className="text-xs text-gray-500 flex items-center gap-1">
                <FaInfoCircle size={12} />
                <span>Hover cards for details</span>
              </div>
            </div>

            <div
              className={`grid gap-4 ${onwardBrandedFares.length <= 3
                  ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
                  : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
                }`}
            >
              {onwardBrandedFares.map((fareTier, index) => (
                <div
                  key={`fare-${index}`}
                  className={`transition-all duration-200 ${selectedIndex === index ? "scale-[1.02]" : ""
                    }`}
                >
                  <FlippableFareCard
                    fare={fareTier}
                    brandFeatures={fareTier.brandFeatures ?? []}
                    brandName={fareTier.label}
                    selected={selectedIndex === index}
                    onClick={() => setSelectedIndex(index)}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3 pt-3 border-t border-gray-200">
              <div className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
                <span className="font-medium">{tier?.label || "N/A"}</span>
                <span className="text-gray-300">|</span>
                <span className="flex items-center gap-1">
                  <FaExchangeAlt size={11} />
                  {changePolicyText}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBookingPopup(true);
                }}
                className="bg-primary text-white font-bold px-8 py-2.5 rounded hover:bg-blue-800 transition-colors"
              >
                Book Now
              </button>
            </div>
          </div>
        )}

        {/* ── View Flight Details accordion ── */}
        {!isPassengerDetailPage && !disableExpansion && (
          <FlightDetailsAccordion
            flightData={flightData}
            tier={tier}
            baseDepartureDate={baseDepartureDate}
            fallbackBaggage={displayedBaggage}
            fallbackCurrency={currency}
            fallbackBaseFare={baseFare}
            fallbackTaxes={taxes}
            fallbackTotal={amount}
          />
        )}
      </div>
    </>
  );
};

export default FlightCard;